// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { authHelper } from '../lib/authHelper';
import type { User } from '../types/schema';

interface AuthContextType {
  user: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  isAdmin: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userData: null,
  loading: true,
  isAdmin: false,
  logout: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribeUser: (() => void) | undefined;

    const unsubscribeAuth = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        try {
          const matchedUsers = await authHelper.findUsersByPhoneNumber(currentUser.phoneNumber);

          if (matchedUsers.length === 0) {
            setError('Det här telefonnumret är inte aktiverat i systemet. Kontakta en administratör.');
            await auth.signOut();
            setUser(null);
            setUserData(null);
            setLoading(false);
            return;
          }

          if (matchedUsers.length > 1) {
            setError('Det finns flera användare med samma telefonnummer. Kontakta en administratör.');
            await auth.signOut();
            setUser(null);
            setUserData(null);
            setLoading(false);
            return;
          }

          const matchedUser = matchedUsers[0];
          try {
            await authHelper.upsertAuthLink(currentUser.uid, matchedUser.id, currentUser.phoneNumber);
          } catch (authLinkError) {
            console.warn('Could not save auth link, continuing auth session:', authLinkError);
          }

          try {
            await authHelper.updateUserLastLogin(matchedUser.id);
          } catch (lastLoginError) {
            console.warn('Could not update last login timestamp, continuing auth session:', lastLoginError);
          }

          // Set up real-time listener for user data
          const userRef = doc(db, 'users', matchedUser.id);
          unsubscribeUser = onSnapshot(
            userRef, 
            (userDoc) => {
              if (userDoc.exists()) {
                const data = userDoc.data();
                setUserData({ id: userDoc.id, ...data } as User);
                setError(null);
              } else {
                setError('Användaren finns inte längre i systemet.');
                auth.signOut().catch((signOutError) => {
                  console.error('Error signing out removed user:', signOutError);
                });
              }
            },
            (err) => {
              console.error('Error in snapshot listener:', err);
              setError(err.message);
            }
          );
        } catch (error: any) {
          console.error('Error in auth state change:', error);
          setError(error.message);
        }
      } else {
        setUserData(null);
        if (unsubscribeUser) {
          unsubscribeUser();
        }
      }
      
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) {
        unsubscribeUser();
      }
    };
  }, []);

  const logout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Failed to log out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        userData, 
        loading, 
        isAdmin: userData?.role === 'admin', 
        logout 
      }}
    >
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
